// src/views/proprio/ProprioAccueilView.jsx — ACCUEIL propriétaire v2 (refonte
// lot ②, spec Dimitri 2026-08-22 §3 + arbitrages du même jour).
//
// « Un propriétaire ouvre son espace pour savoir combien il gagne et si son
// bien est bien tenu. Pas pour lire une liste de tuiles. »
//
// LE BANDEAU (rendu ICI, pas par le shell — il EST le bandeau navy de la page) :
//   · « Bonjour Marie » + la date (« 22 août » — jamais de jour de semaine) ;
//   · LE MONTANT EN GRAND (46px / 36 téléphone) + « € » à .5, « de revenus en
//     août » dessous ; LA VARIATION en encart (flèche, « +12 % », « vs juillet ») ;
//   · ⚠️ LA GARDE DU HÉRO (arbitrage 1) : la facturation du mois regardé n'est
//     pas établie (Σ montants = 0 ET nuits > 0) → la phrase « La facturation
//     d'août n'est pas encore établie. » REMPLACE le montant, la variation se
//     tait. RÈGLE : jamais un zéro qui ressemble à une perte.
//   · Le montant lit LA MÊME SOURCE PROJETÉE que les stats (proprio_stats
//     parMois.caBrut) — plus jamais le blob brut (incohérence v2 corrigée).
//   · Sous un filet : trois chiffres nus — nuits / occupation / note.
//
// SES BIENS : une carte par bien — PHOTO à gauche (couverture livret, sinon
// vignette NEUTRE — jamais une image fausse), statut à pastille, trois chiffres
// (revenu du mois « — » si indisponible, jours occupés, note), pied utile.
//
// AUJOURD'HUI : une ligne verte quand tout va bien (ménage fait · terminé à
// 12h30 · 18 points sur 18 · bouton photos) ; incident du jour en terracotta.
//
// PAS de « Nous écrire », PAS de « Bloquer des dates » (retirés, spec §3).
// LECTURE SEULE stricte — aucun recompute de montant, on LIT le serveur.

import { useCallback, useEffect, useState } from 'react'
import { compterPoints, compterConfigurees } from '../../lib/checklist-lecture'
import { Star, AlertTriangle, Home, Camera, Sparkles } from 'lucide-react'
import { DS } from '../../lib/ui'
import { MONTHS } from '../../lib/format'
import { Loading, ErrorBox } from './_proprioUi'
import useEtroit from '../../lib/useEtroit'
import ProprioPhotoViewer from './ProprioPhotoViewer'
import {
  fetchProprioStats,
  fetchProprioAvis,
  fetchProprioCalendar,
  fetchProprioMenage,
  fetchProprioIncidents,
  fetchProprioLogements,
} from '../../lib/proprio-api'

// ════════════════════════════════════════════════════════════════════════════
// DÉRIVATIONS PURES (exportées pour les tests) — présentation uniquement.
// ════════════════════════════════════════════════════════════════════════════

// Date du jour ISO 'YYYY-MM-DD' en Europe/Paris (DST-aware, Intl 'en-CA').
export function todayParisIso(now = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(now)
}

// Clé de mois 'YYYY-MM' d'une date ISO.
export function monthKeyOf(iso) {
  return String(iso || '').slice(0, 7)
}

// Mois calendaire strictement précédent ('YYYY-MM' → 'YYYY-MM'). Gère janvier.
export function prevMonthKey(monthKey) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(monthKey || ''))
  if (!m) return null
  let y = Number(m[1])
  let mo = Number(m[2]) - 1
  if (mo < 1) { mo = 12; y -= 1 }
  return `${y}-${String(mo).padStart(2, '0')}`
}

// Variation % mois N vs N-1 (présentation pure) : prev manquant ou ≤ 0 → null
// (jamais un « +∞ » trompeur) ; sinon arrondi entier signé.
export function computeRevenueVariation(curr, prev) {
  if (prev == null) return null
  const p = Number(prev)
  if (!(p > 0)) return null
  const c = Number(curr) || 0
  const pct = Math.round(((c - p) / p) * 100)
  const dir = pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat'
  const sign = pct > 0 ? '+' : pct < 0 ? '−' : ''
  return { pct, dir, label: `${sign}${Math.abs(pct)} %` }
}

// ⚠️ LA GARDE DU HÉRO (arbitrage Dimitri 2026-08-22, mesuré en base) :
//   'indisponible' — caBrut null (périmètre KV dégradé) → « — », variation tue ;
//   'pas_etablie'  — caBrut = 0 ET nuits > 0 : les résas sont là (dates iCal)
//                    mais AUCUN montant n'est posé → la phrase remplace le
//                    montant, la variation se tait (le « −100 % » de l'incident
//                    du 22/08 comparait un juillet réel à un août pas établi) ;
//   'etabli'       — montant affichable (y compris un vrai 0 sans nuits).
export function etatMontantHero(caBrut, nuits) {
  if (caBrut == null) return 'indisponible'
  if (Number(caBrut) === 0 && Number(nuits) > 0) return 'pas_etablie'
  return 'etabli'
}

// Montant entier fr-FR (« 2 340 ») — les centimes vivent dans Mes revenus.
export function fmtMontantEntier(v) {
  if (v == null || Number.isNaN(Number(v))) return '—'
  return Math.round(Number(v)).toLocaleString('fr-FR')
}

// Statut d'un bien aujourd'hui depuis les réservations chargées (today → +90 j).
// « occupied » (départ du séjour courant) · « upcoming » (prochaine arrivée) ·
// « none ». Annulées exclues. Pur.
export function bienStatusOf(logId, reservations, todayIso) {
  const today = String(todayIso || '')
  let current = null
  let next = null
  for (const r of Array.isArray(reservations) ? reservations : []) {
    if (!r || r.logement_id !== logId) continue
    if (/cancel|annul/i.test(String(r.status || ''))) continue
    const ci = String(r.start_date || '').slice(0, 10)
    const co = String(r.end_date || '').slice(0, 10)
    if (!ci || !co) continue
    if (ci <= today && today <= co) {
      if (!current || co > current.co) current = { ci, co, r }
    } else if (ci > today) {
      if (!next || ci < next.ci) next = { ci, co, r }
    }
  }
  if (current) return { kind: 'occupied', date: current.co, resa: current.r }
  if (next) return { kind: 'upcoming', date: next.ci, resa: next.r }
  return { kind: 'none', date: null, resa: null }
}

// 'YYYY-MM-DD' → « 15 juin » (jour + mois, sans année ni jour de semaine —
// règle des dates). Construit depuis les parties (0 décalage de fuseau).
export function fmtDayMonthFR(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''))
  if (!m) return ''
  const mon = MONTHS[Number(m[2]) - 1] || ''
  return `${Number(m[3])} ${mon.toLowerCase()}`
}

// Une résa est-elle en cours aujourd'hui ? (bornes incluses, annulées exclues)
export function isStayInProgress(resa, todayIso) {
  if (!resa) return false
  if (/cancel|annul/i.test(String(resa.status || ''))) return false
  const ci = String(resa.start_date || '').slice(0, 10)
  const co = String(resa.end_date || '').slice(0, 10)
  if (!ci || !co) return false
  const t = String(todayIso || '')
  return ci <= t && t <= co
}

export function isOnDay(dateStr, todayIso) {
  return String(dateStr || '').slice(0, 10) === String(todayIso || '')
}

export function filterOnDay(items, dateField, todayIso) {
  return (Array.isArray(items) ? items : []).filter((it) => it && isOnDay(it[dateField], todayIso))
}

// Nuits d'une résa (différence de dates, jamais un champ inventé).
export function nuitsDeResa(resa) {
  const ci = String(resa?.start_date || '').slice(0, 10)
  const co = String(resa?.end_date || '').slice(0, 10)
  if (!ci || !co) return null
  const n = Math.round((new Date(`${co}T00:00:00Z`) - new Date(`${ci}T00:00:00Z`)) / 86400000)
  return n > 0 ? n : null
}

// LE PIED de la carte d'un bien — la précision utile (spec §3). Une donnée
// absente (nb personnes en KV) est OMISE, jamais « 0 personne ».
export function piedBien(statut) {
  if (!statut || statut.kind === 'none') return 'Aucune réservation à venir'
  const r = statut.resa || {}
  const nom = [r.guest_first_name, r.guest_last_name].filter(Boolean).join(' ').trim()
  if (statut.kind === 'occupied') {
    const parts = [nom || null]
    if (r.guest_count != null) parts.push(`${r.guest_count} personne${Number(r.guest_count) > 1 ? 's' : ''}`)
    parts.push(`départ ${fmtDayMonthFR(statut.date)}`)
    return parts.filter(Boolean).join(' · ')
  }
  const parts = [`Prochaine arrivée : ${nom || fmtDayMonthFR(statut.date)}`]
  const n = nuitsDeResa(r)
  if (n) parts.push(`${n} nuit${n > 1 ? 's' : ''}`)
  return parts.join(' · ')
}

// « 12h30 » depuis un timestamp ISO (Europe/Paris, heure française).
export function fmtHeureFR(ts) {
  if (!ts) return null
  try {
    const s = new Intl.DateTimeFormat('fr-FR', {
      timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit',
    }).format(new Date(ts))
    return s.replace(':', 'h')
  } catch {
    return null
  }
}

// « 18 points sur 18 » — items cochés vs lignes CONFIGURÉES du logement.
// ⚠️ CE SEGMENT NE S'AFFICHAIT JAMAIS (défaut Dimitri 2026-08-22) : il lisait
// `items_checked` comme une LISTE alors que la base stocke un OBJET par pièce.
// La lecture passe désormais par le module partagé — un champ se lit d'une
// seule manière (src/lib/checklist-lecture.js). Rien de configuré, ou rien de
// coché → null : le segment est omis, jamais un compte inventé.
export function pointsChecklist(run, templatesByLog) {
  const { coches } = compterPoints(run?.items_checked)
  const total = compterConfigurees(templatesByLog?.[run?.logement_id])
  if (!total || !coches) return null
  return `${coches} points sur ${total}`
}

function fmtNote(v) {
  return v == null || Number.isNaN(Number(v)) ? '—' : Number(v).toFixed(2).replace(/0$/, '').replace('.', ',')
}

// « de juillet » / « d'août » — élision devant voyelle (présentation pure).
export function deMois(moisNom) {
  return /^[aeiouyâàéèêëîïôöûü]/i.test(String(moisNom || '')) ? `d'${moisNom}` : `de ${moisNom}`
}

// ════════════════════════════════════════════════════════════════════════════
// VUE
// ════════════════════════════════════════════════════════════════════════════

const EMPTY = {
  loading: true, error: null,
  caCurrent: null, caPrev: null, nuits: null, nuitsDispo: null, occupation: null,
  note: { avg: null, count: 0 }, statsByLogAvis: {},
  reservations: [], runs: [], templatesByLog: {}, incidents: [], logements: [],
  parBien: {},
}

const CSS = `
.fbz-acc2-biens { display: grid; grid-template-columns: 1fr; gap: 10px; }
@media (min-width: 620px) { .fbz-acc2-biens { grid-template-columns: 1fr 1fr; } }
`

export default function ProprioAccueilView({ currentUser }) {
  const etroit = useEtroit()
  const [state, setState] = useState(EMPTY)
  const [visionneuse, setVisionneuse] = useState(null) // { photos, index }

  const todayIso = todayParisIso()
  const month = monthKeyOf(todayIso)
  const prevMonth = prevMonthKey(month)
  const windowEnd = todayParisIso(new Date(Date.now() + 90 * 86400000))

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }))
    const results = await Promise.allSettled([
      fetchProprioStats({ from: prevMonth, to: month }), // 0 CA projeté (2 mois)
      fetchProprioAvis(),                                 // 1 note
      fetchProprioCalendar({ from: todayIso, to: windowEnd }), // 2 statuts biens
      fetchProprioMenage(),                               // 3 ménage du jour
      fetchProprioIncidents(),                            // 4 incidents
      fetchProprioLogements(),                            // 5 biens + photos
    ])
    const ok = (i) => (results[i].status === 'fulfilled' ? results[i].value : null)
    if (results.every((r) => r.status === 'rejected')) {
      const first = results.find((r) => r.status === 'rejected')
      setState((s) => ({ ...s, loading: false, error: (first && first['reason'] && first['reason'].message) || 'Chargement impossible.' }))
      return
    }
    const stats = ok(0)
    const avis = ok(1)
    const cal = ok(2)
    const menage = ok(3)
    const inc = ok(4)
    const logs = ok(5)

    const pmCur = stats?.parMois?.[month] || {}
    const pmPrev = stats?.parMois?.[prevMonth] || {}
    const logements = logs?.logements || []

    // Chiffres PAR BIEN — un appel stats cloisonné par logement (périmètre
    // petit ; un logement KV seul ressort caBrut null → « — » honnête).
    const parBien = {}
    await Promise.all(logements.map(async (l) => {
      try {
        const s = await fetchProprioStats({ from: month, to: month, logementId: l.id })
        const pm = s?.parMois?.[month] || {}
        parBien[l.id] = { ca: pm.caBrut ?? null, nuits: pm.nuitsLouees ?? null }
      } catch {
        parBien[l.id] = { ca: null, nuits: null }
      }
    }))

    setState({
      loading: false, error: null,
      caCurrent: pmCur.caBrut ?? null,
      caPrev: pmPrev.caBrut ?? null,
      nuits: pmCur.nuitsLouees ?? null,
      nuitsDispo: pmCur.nuitsDispo ?? null,
      occupation: pmCur.occupation ?? null,
      note: avis?.statsOverall || { avg: null, count: 0 },
      statsByLogAvis: avis?.statsByLog || {},
      reservations: cal?.reservations || [],
      runs: menage?.runs || [],
      templatesByLog: menage?.templatesByLog || {},
      incidents: inc?.incidents || [],
      logements,
      parBien,
    })
  }, [month, prevMonth, todayIso, windowEnd])

  useEffect(() => { load() }, [load])

  if (state.loading) return <Loading label="Chargement de votre tableau de bord…" />
  if (state.error) return <ErrorBox message={state.error} onRetry={load} />

  const prenom = (currentUser?.first_name || currentUser?.prenom || '').trim()
  const moisNom = (MONTHS[Number(month.slice(5, 7)) - 1] || '').toLowerCase()
  const moisPrevNom = prevMonth ? (MONTHS[Number(prevMonth.slice(5, 7)) - 1] || '').toLowerCase() : ''
  const etat = etatMontantHero(state.caCurrent, state.nuits)
  const variation = etat === 'etabli' ? computeRevenueVariation(state.caCurrent, state.caPrev) : null

  const runsJour = filterOnDay(state.runs, 'scheduled_date', todayIso)
  const runsFaits = runsJour.filter((r) => r.status === 'completed')
  const runsPrevus = runsJour.filter((r) => r.status !== 'completed')
  const incidentsJour = filterOnDay(state.incidents, 'incident_date', todayIso)
  const nomDe = (lid) => state.logements.find((l) => l.id === lid)?.nom || 'votre logement'

  return (
    <div style={{ fontFamily: DS.fontBody }}>
      <style>{CSS}</style>

      {/* ── LE BANDEAU — c'est LE bandeau navy de la page (le shell s'efface). ── */}
      <div data-testid="accueil-bandeau"
        style={{ position: 'relative', overflow: 'hidden', color: '#fff',
          background: 'linear-gradient(115deg, #233240 0%, #3d5166 45%, #5c8ab8 100%)',
          borderRadius: etroit ? 0 : 12,
          margin: etroit ? '0 -12px 14px' : '0 0 16px',
          padding: etroit ? '16px 14px 14px' : '18px 20px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: DS.fontDisplay, fontSize: 20, fontWeight: 700 }}>
            {prenom ? `Bonjour ${prenom}` : 'Bonjour'}
          </span>
          <span style={{ fontSize: 11.5, opacity: 0.8 }}>{fmtDayMonthFR(todayIso)}</span>
        </div>

        {/* Le montant — ou sa vérité quand il n'existe pas encore. */}
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {etat === 'pas_etablie' ? (
            <div data-testid="hero-pas-etablie" style={{ fontSize: 12, lineHeight: 1.5, opacity: 0.95, maxWidth: 340 }}>
              La facturation {deMois(moisNom)} n'est pas encore établie.
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                <span data-testid="hero-montant"
                  style={{ fontFamily: DS.fontDisplay, fontSize: etroit ? 36 : 46, fontWeight: 700, lineHeight: 1 }}>
                  {etat === 'etabli' ? fmtMontantEntier(state.caCurrent) : '—'}
                </span>
                {etat === 'etabli' && <span style={{ fontSize: 22, opacity: 0.5, fontWeight: 600 }}>€</span>}
              </div>
              <div style={{ fontSize: 11.5, opacity: 0.85, marginTop: 3 }}>de revenus en {moisNom}</div>
            </div>
          )}
          {variation && (
            <span data-testid="hero-variation"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 9px',
                borderRadius: 9, border: '1px solid rgba(255,255,255,.16)',
                background: variation.dir === 'down' ? 'rgba(184,65,43,.30)' : 'rgba(127,196,163,.28)',
                fontSize: 12 }}>
              <span style={{ color: variation.dir === 'down' ? '#ffc2b3' : '#a8dcc4', fontWeight: 700 }}>
                {variation.dir === 'up' ? '▲' : variation.dir === 'down' ? '▼' : '■'} {variation.label}
              </span>
              <span style={{ opacity: 0.8 }}>vs {moisPrevNom}</span>
            </span>
          )}
        </div>

        {/* Sous un filet : trois chiffres nus — eux sont vrais. */}
        <div style={{ display: 'flex', gap: etroit ? 22 : 34, marginTop: 13, paddingTop: 11,
          borderTop: '1px solid rgba(255,255,255,.14)' }}>
          {[
            [state.nuits == null ? '—' : `${state.nuits} nuits`, state.nuitsDispo == null ? '' : `sur ${state.nuitsDispo}`],
            [state.occupation == null ? '—' : `${Math.round(Number(state.occupation))} %`, "d'occupation"],
            [state.note?.avg == null ? '—' : fmtNote(state.note.avg), 'de note'],
          ].map(([grand, petit], i) => (
            <div key={i}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{grand}</div>
              {petit ? <div style={{ fontSize: 10, opacity: 0.75, marginTop: 1 }}>{petit}</div> : null}
            </div>
          ))}
        </div>
      </div>

      {/* ── SES BIENS — une carte par bien, la photo rend l'écran vivant. ── */}
      <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.07em', color: '#5c7d99',
        textTransform: 'uppercase', margin: '2px 2px 8px' }}>
        Vos biens · {state.logements.length}
      </div>
      <div className="fbz-acc2-biens" style={{ marginBottom: 18 }}>
        {state.logements.map((log) => {
          const statut = bienStatusOf(log.id, state.reservations, todayIso)
          const chiffres = state.parBien[log.id] || { ca: null, nuits: null }
          const noteBien = state.statsByLogAvis?.[log.id]?.avg ?? null
          const occupe = statut.kind === 'occupied'
          return (
            <div key={log.id} data-testid={`bien-${log.id}`}
              style={{ display: 'flex', border: '1px solid #e5eaf0', borderRadius: 12,
                overflow: 'hidden', background: '#fff' }}>
              {log.photo ? (
                <img src={log.photo} alt="" aria-hidden="true"
                  style={{ width: etroit ? 86 : 100, alignSelf: 'stretch', objectFit: 'cover', flexShrink: 0 }} />
              ) : (
                <div aria-hidden="true"
                  style={{ width: etroit ? 86 : 100, alignSelf: 'stretch', flexShrink: 0,
                    background: 'linear-gradient(160deg, #eef4fa, #dbeaf7)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Home size={24} strokeWidth={1.6} color="#87b8e6" />
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '10px 12px 8px' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#233240' }}>{log.nom}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3,
                    fontSize: 11, fontWeight: 600, color: occupe ? '#3d6b57' : '#98a2ad' }}>
                    <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%',
                      background: occupe ? '#3d6b57' : '#98a2ad', display: 'inline-block' }} />
                    {statut.kind === 'occupied' && `occupé jusqu'au ${fmtDayMonthFR(statut.date)}`}
                    {statut.kind === 'upcoming' && `libre jusqu'au ${fmtDayMonthFR(statut.date)}`}
                    {statut.kind === 'none' && 'libre'}
                  </div>
                  <div style={{ display: 'flex', gap: 18, marginTop: 9 }}>
                    {[
                      [chiffres.ca == null ? '—' : `${fmtMontantEntier(chiffres.ca)} €`, 'ce mois'],
                      [chiffres.nuits == null ? '—' : chiffres.nuits, 'jours occupés'],
                      [noteBien == null ? '—' : fmtNote(noteBien), 'de note'],
                    ].map(([grand, petit], i) => (
                      <div key={i}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#233240' }}>{grand}</div>
                        <div style={{ fontSize: 9, color: '#5c6773', marginTop: 1 }}>{petit}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ marginTop: 'auto', background: '#f8fafc', borderTop: '1px solid #eef2f6',
                  padding: '7px 12px', fontSize: 10.5, color: '#3d5166' }}>
                  {piedBien(statut)}
                </div>
              </div>
            </div>
          )
        })}
        {!state.logements.length && (
          <div style={{ border: '1px dashed #d0d7de', borderRadius: 12, padding: 18,
            fontSize: 12, color: '#5c6773' }}>
            Aucun logement rattaché à votre compte pour l'instant.
          </div>
        )}
      </div>

      {/* ── AUJOURD'HUI ── */}
      <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.07em', color: '#5c7d99',
        textTransform: 'uppercase', margin: '2px 2px 8px' }}>
        Aujourd'hui
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {runsFaits.map((r) => {
          const heure = fmtHeureFR(r.completed_at)
          const points = pointsChecklist(r, state.templatesByLog)
          const photos = Array.isArray(r.photos) ? r.photos.filter((p) => p && p.url) : []
          return (
            <div key={r.id} data-testid={`jour-menage-${r.id}`}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                background: '#f0f7f3', border: '1px solid #c9e0d4', borderRadius: 10 }}>
              <Sparkles size={15} strokeWidth={2} color="#124a33" style={{ flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 12, color: '#124a33', fontWeight: 600 }}>
                {['Ménage fait à ' + nomDe(r.logement_id), heure ? `terminé à ${heure}` : null, points]
                  .filter(Boolean).join(' · ')}
              </span>
              {photos.length > 0 && (
                <button type="button" data-testid={`jour-photos-${r.id}`}
                  onClick={() => setVisionneuse({ photos, index: 0 })}
                  className="fbz-press"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 10px',
                    background: '#fff', border: '1px solid #c9e0d4', borderRadius: 8,
                    fontSize: 11, fontWeight: 700, color: '#124a33', cursor: 'pointer',
                    fontFamily: DS.fontBody, flexShrink: 0 }}>
                  <Camera size={13} strokeWidth={2} />
                  {photos.length} photo{photos.length > 1 ? 's' : ''}
                </button>
              )}
            </div>
          )
        })}
        {runsPrevus.map((r) => (
          <div key={r.id} data-testid={`jour-menage-prevu-${r.id}`}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              background: '#f8fafc', border: '1px solid #e5eaf0', borderRadius: 10,
              fontSize: 12, color: '#3d5166', fontWeight: 600 }}>
            <Sparkles size={15} strokeWidth={2} color="#5c8ab8" style={{ flexShrink: 0 }} />
            Ménage prévu à {nomDe(r.logement_id)} aujourd'hui
          </div>
        ))}
        {incidentsJour.map((inc) => (
          <div key={inc.id} data-testid={`jour-incident-${inc.id}`}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              background: '#fdf4f0', border: '1px solid #e0b5a5', borderRadius: 10,
              fontSize: 12, color: '#8f2f1d', fontWeight: 600 }}>
            <AlertTriangle size={15} strokeWidth={2} color="#8f2f1d" style={{ flexShrink: 0 }} />
            Incident signalé à {nomDe(inc.logement_id)}{inc.label ? ` · ${inc.label}` : ''}
          </div>
        ))}
        {!runsJour.length && !incidentsJour.length && (
          <div data-testid="jour-rien"
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              background: '#f0f7f3', border: '1px solid #c9e0d4', borderRadius: 10,
              fontSize: 12, color: '#124a33', fontWeight: 600 }}>
            <Star size={15} strokeWidth={2} color="#124a33" style={{ flexShrink: 0 }} />
            Rien à signaler aujourd'hui.
          </div>
        )}
      </div>

      {visionneuse && (
        <ProprioPhotoViewer
          photos={visionneuse.photos}
          index={visionneuse.index}
          onIndex={(i) => setVisionneuse((v) => (v ? { ...v, index: i } : v))}
          onClose={() => setVisionneuse(null)} />
      )}
    </div>
  )
}
